import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Copy, Eye, EyeOff, ExternalLink, LockKeyhole, Rocket, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../api";
import { SITE_PREVIEW_KEY } from "../../components/SiteGate";

const DEFAULT_COPY = {
  headline: "A new chapter is taking shape.",
  message: "Our digital atelier is being prepared with care. We look forward to welcoming you soon.",
};

function formatIst(iso) {
  if (!iso) return "Not scheduled";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(iso));
}

function toIstInput(iso) {
  if (!iso) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const get = (type) => parts.find((part) => part.type === type)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

function statusLabel(status) {
  if (status?.is_live) return "Website is live";
  if (status?.mode === "scheduled") return "Launch scheduled";
  return "Coming Soon is active";
}

export default function AdminLaunch() {
  const [status, setStatus] = useState(null);
  const [scheduleValue, setScheduleValue] = useState("");
  const [copy, setCopy] = useState(DEFAULT_COPY);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [loadError, setLoadError] = useState("");
  const [previewPassword, setPreviewPassword] = useState("");

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const { data } = await api.getOnce("/api/admin/site-launch", { cache: "no-store" });
      setStatus(data);
      setLoadError("");
      if (!quiet) {
        setScheduleValue(toIstInput(data.scheduled_at));
        setCopy({ headline: data.headline || DEFAULT_COPY.headline, message: data.message || DEFAULT_COPY.message });
      }
    } catch (err) {
      const message = formatApiError(err);
      setLoadError(message);
      if (!quiet) toast.error(message);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = window.setInterval(() => load({ quiet: true }), 30000);
    const onFocus = () => load({ quiet: true });
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  const update = async (mode, scheduledAt, successMessage) => {
    if (!status || loadError) return toast.error("Refresh launch status before making changes");
    setAction(mode);
    try {
      const { data } = await api.put("/api/admin/site-launch", {
        mode,
        scheduled_at: scheduledAt,
        headline: copy.headline.trim(),
        message: copy.message.trim(),
        expected_revision: status?.revision,
      });
      setStatus(data);
      setLoadError("");
      setScheduleValue(toIstInput(data.scheduled_at));
      toast.success(successMessage);
    } catch (err) {
      toast.error(formatApiError(err));
      if (err?.response?.status === 409) load();
    } finally {
      setAction("");
    }
  };

  const saveCopy = () => {
    if (!copy.headline.trim() || !copy.message.trim()) return toast.error("Headline and message are required");
    const mode = status?.mode || "construction";
    return update(mode, mode === "scheduled" ? status.scheduled_at : null, "Coming Soon content saved");
  };

  const scheduleLaunch = () => {
    if (status?.is_live) return toast.error("Show Coming Soon again before scheduling a future launch");
    if (!scheduleValue) return toast.error("Select an IST launch date and time");
    const scheduledAt = `${scheduleValue}:00+05:30`;
    if (Date.parse(scheduledAt) <= Date.now()) return toast.error("Select a future date and time");
    return update("scheduled", scheduledAt, "Automatic launch scheduled");
  };

  const launchNow = async () => {
    if (!status || loadError) return toast.error("Refresh launch status before launching");
    if (!window.confirm("Launch the complete website publicly now?")) return;
    setAction("live");
    try {
      const { data } = await api.put("/api/admin/site-launch/launch-now", {
        expected_revision: status?.revision,
      });
      setStatus(data);
      setLoadError("");
      setScheduleValue("");
      toast.success("Website is now live");
    } catch (err) {
      toast.error(formatApiError(err));
      if (err?.response?.status === 409) load();
    } finally {
      setAction("");
    }
  };

  const closeSite = () => {
    if (!window.confirm("Hide the public website and show Coming Soon to visitors?")) return;
    return update("construction", null, "Coming Soon page is now active");
  };

  const previewSite = () => {
    if (status?.is_live) {
      window.location.assign("/");
      return;
    }
    try {
      sessionStorage.setItem(SITE_PREVIEW_KEY, "1");
    } catch {
      toast.error("Private preview could not start in this browser. Allow session storage and try again.");
      return;
    }
    window.location.assign("/");
  };

  const saveClientPreview = async (enabled) => {
    if (!status || loadError) return toast.error("Refresh launch status before changing client access");
    if (enabled && !status.preview_enabled && previewPassword.trim().length < 8) {
      return toast.error("Create a preview password with at least 8 characters");
    }
    setAction(enabled ? "preview-enable" : "preview-disable");
    try {
      const { data } = await api.put("/api/admin/site-preview", {
        enabled,
        password: previewPassword.trim() || null,
        expected_revision: status.revision,
      });
      setStatus(data);
      setPreviewPassword("");
      setLoadError("");
      toast.success(enabled ? "Client preview access is ready" : "Client preview access disabled");
    } catch (err) {
      toast.error(formatApiError(err));
      if (err?.response?.status === 409) load();
    } finally {
      setAction("");
    }
  };

  const copyPreviewLink = async () => {
    const previewUrl = `${window.location.origin}/preview`;
    try {
      await navigator.clipboard.writeText(previewUrl);
      toast.success("Client preview link copied");
    } catch {
      window.prompt("Copy this client preview link", previewUrl);
    }
  };

  if (loading) return <div className="admin-page"><p>Loading launch controls&hellip;</p></div>;
  if (!status) {
    return (
      <div className="admin-page launch-admin-page">
        <div className="admin-page-head"><div><span className="eyebrow">Private preparation &amp; publishing</span><h1>Launch control.</h1></div></div>
        <section className="admin-form-section launch-load-error" role="alert">
          <h2>Launch status could not be loaded</h2>
          <p>{loadError || "Please check the backend connection and try again."}</p>
          <button type="button" className="button dark-button" onClick={() => load()}>Retry safely</button>
        </section>
      </div>
    );
  }

  const controlsDisabled = Boolean(action) || Boolean(loadError);

  return (
    <div className="admin-page launch-admin-page">
      <div className="admin-page-head">
        <div>
          <span className="eyebrow">Private preparation &amp; publishing</span>
          <h1>Launch control.</h1>
        </div>
        <span className={`launch-status-pill ${status?.is_live ? "live" : status?.mode || "construction"}`}>
          {statusLabel(status)}
        </span>
      </div>

      <div className="launch-options-grid">
        <section className="admin-form-section launch-option-card">
          <div className="launch-option-number">Option 01</div>
          <Eye size={25} />
          <h2>Prepare &amp; preview privately</h2>
          <p>{status.is_live ? "Edit every product, category and website section, then review the live storefront." : "Edit every product, category and website section while visitors continue to see the Coming Soon page."}</p>
          <div className="launch-edit-links">
            <Link to="/admin/products">Manage products</Link>
            <Link to="/admin/categories">Manage categories</Link>
            <Link to="/admin/homepage">Edit home page</Link>
            <Link to="/admin/settings">Edit website details</Link>
          </div>
          <button type="button" className="button outline-button" onClick={previewSite} data-testid="private-preview-button">
            <Eye size={15} /> {status.is_live ? "Open live website" : "Preview complete website"} <ExternalLink size={14} />
          </button>
          <small>{status.is_live ? "The website is already visible to all visitors." : "Private preview works only in this signed-in admin session. It does not publish the site."}</small>
        </section>

        <section className="admin-form-section launch-option-card launch-publish-card">
          <div className="launch-option-number">Option 02</div>
          <Rocket size={25} />
          <h2>Publish when ready</h2>
          <p>Launch instantly, or select an India time for the complete website to open automatically.</p>
          {status?.mode === "scheduled" && (
            <div className="launch-scheduled-note"><CalendarClock size={16} /> Scheduled for {formatIst(status.scheduled_at)} IST</div>
          )}
          <label className="launch-datetime-label">
            Automatic launch date &amp; time (IST)
            <input type="datetime-local" value={scheduleValue} onChange={(e) => setScheduleValue(e.target.value)} disabled={status.is_live || controlsDisabled} data-testid="launch-datetime-input" />
          </label>
          <div className="launch-button-stack">
            <button type="button" className="button outline-button" onClick={scheduleLaunch} disabled={status.is_live || controlsDisabled} data-testid="schedule-launch-button">
              <CalendarClock size={15} /> {action === "scheduled" ? "Scheduling…" : "Schedule automatic launch"}
            </button>
            <button type="button" className="button dark-button" onClick={launchNow} disabled={controlsDisabled || status?.is_live} data-testid="launch-now-button">
              <Rocket size={15} /> {status?.is_live ? "Website is live" : action === "live" ? "Launching…" : "Launch website now"}
            </button>
            {(status?.is_live || status?.mode === "scheduled") && (
              <button type="button" className="button quiet-danger-button" onClick={closeSite} disabled={controlsDisabled} data-testid="close-site-button">
                <EyeOff size={15} /> Show Coming Soon again
              </button>
            )}
          </div>
        </section>
      </div>

      <section className="admin-form-section launch-client-preview-card">
        <div className="launch-client-preview-heading">
          <div>
            <span className="eyebrow">Password-protected storefront review</span>
            <h2>Client preview access</h2>
          </div>
          <span className={`launch-status-pill ${status.preview_enabled ? "live" : "construction"}`}>
            {status.preview_enabled ? "Access enabled" : "Access disabled"}
          </span>
        </div>
        <p>
          Share a separate preview link and password with the client. They can review the complete frontend while public visitors see Coming Soon, but they cannot open the admin panel.
        </p>
        <div className="launch-client-preview-grid">
          <label>
            Client preview link
            <div className="launch-preview-link-row">
              <input value={`${window.location.origin}/preview`} readOnly data-testid="client-preview-link" />
              <button type="button" className="button outline-button" onClick={copyPreviewLink}><Copy size={14} /> Copy link</button>
            </div>
          </label>
          <label>
            {status.preview_enabled ? "New password (leave blank to keep current password)" : "Create preview password"}
            <input
              type="password"
              autoComplete="new-password"
              minLength={8}
              maxLength={128}
              value={previewPassword}
              onChange={(event) => setPreviewPassword(event.target.value)}
              placeholder="Minimum 8 characters"
              data-testid="client-preview-admin-password"
            />
          </label>
        </div>
        <div className="launch-client-preview-actions">
          <button
            type="button"
            className="button dark-button"
            onClick={() => saveClientPreview(true)}
            disabled={controlsDisabled}
            data-testid="enable-client-preview"
          >
            <LockKeyhole size={15} /> {action === "preview-enable" ? "Saving…" : status.preview_enabled ? "Save new password" : "Enable client preview"}
          </button>
          {status.preview_enabled && (
            <button
              type="button"
              className="button quiet-danger-button"
              onClick={() => saveClientPreview(false)}
              disabled={controlsDisabled}
              data-testid="disable-client-preview"
            >
              <EyeOff size={15} /> {action === "preview-disable" ? "Disabling…" : "Disable client access"}
            </button>
          )}
        </div>
        <small>Changing the password or disabling access immediately invalidates all previously opened client preview sessions.</small>
      </section>

      <section className="admin-form-section launch-copy-card">
        <div>
          <span className="eyebrow">Visitor message</span>
          <h2>Coming Soon content</h2>
        </div>
        <div className="form-grid">
          <label className="span-2">
            Headline
            <input value={copy.headline} maxLength={120} onChange={(e) => setCopy({ ...copy, headline: e.target.value })} data-testid="launch-headline-input" />
          </label>
          <label className="span-2">
            Message
            <textarea rows={3} value={copy.message} maxLength={320} onChange={(e) => setCopy({ ...copy, message: e.target.value })} data-testid="launch-message-input" />
          </label>
        </div>
        {loadError && <p className="launch-inline-warning" role="alert">Connection lost. Refresh status before saving changes.</p>}
        <button type="button" className="button outline-button" onClick={saveCopy} disabled={controlsDisabled} data-testid="save-launch-copy-button">
          <Save size={15} /> Save Coming Soon content
        </button>
      </section>
    </div>
  );
}
